import { Spin } from 'antd'
import { Suspense, lazy } from 'react'
import { MdOutlineGavel } from 'react-icons/md'
import { TbLayoutKanban } from 'react-icons/tb'

import { CRM_CONTRACT_FILE_EXTENSIONS, CRM_FILE_MAX_SIZE_MB } from '@/Containers/CrmSection/Components/Contracts/CrmContract'
import { openApplicationCreate } from '@/Containers/TransportationSection/ApplicationContainer/Hooks'
import { createTransportRequestButtons } from '@/Containers/TransportationSection/Components'
import { renderRichTextView } from '@/Ship/Components/RichText/RichTextView'
import { RichTextInput } from '@/Ship/Components/RichText/RichTextInput'
import { StatusDictionarySelect } from '@/Ship/Components/FormFields/StatusDictionarySelect'
import { attachUploadedFiles } from '@/Ship/Helpers/attachUploadedFiles'
import { renderDateTimeView, renderDateView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import { TenderClientLink, TenderStatus } from '../Components/TenderCells'
import { tenderKanbanRows } from '../Components/tenderKanbanRows'
import {
    TENDER_DRAWER_WIDTH,
    TENDER_FILE_UPLOAD_URL,
    TENDER_LOSS_REASON_RESOURCE,
    TENDER_REST_URL,
    TENDER_STATUS_RESOURCE,
} from '../Contracts/ITender'
import { TENDER_FIELD_LABELS as L } from '../Contracts/TenderFieldLabels'
import { TenderFormTemplate } from '../Templates/TenderFormTemplate'

import type { TransportRequestKanbanProps } from '@/Containers/TransportationSection/Components'
import type { TenderRecord } from '../Contracts/ITender'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { ReactNode } from 'react'

const TransportRequestKanban = lazy(() => import('@/Containers/TransportationSection/Components/TransportRequestKanban'))

/** Мінімум headerPage-контексту, потрібний реєстрації режиму «Канбан». */
interface TenderHeaderPageContextLike {
    storages?: {
        crudContext?: {
            listContext?: { variantItems?: Record<string, unknown> }
            resource?: unknown
        } & Record<string, unknown>
    }
}

const TenderKanbanTemplate = (props: Omit<TransportRequestKanbanProps, 'statusResource' | 'rows'>) => (
    <Suspense
        fallback={
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Spin />
            </div>
        }
    >
        <TransportRequestKanban {...props} statusResource={TENDER_STATUS_RESOURCE} rows={tenderKanbanRows} />
    </Suspense>
)

const sourceOf = (row: unknown): TenderRecord => {
    const item = (row ?? {}) as TenderRecord & { _original?: TenderRecord }

    return item._original ?? item
}

/**
 * Колонки, які ядро само не намалює як треба:
 *
 *  - **«Статус»** — бейдж кольору довідника і бейдж «Кількість пропозицій»
 *    (ядровий бейдж шукає статус серед `options` ресурсу, а тут — рядок довідника);
 *  - **«Клієнт»** — посилання на картку клієнта (легасі `TenderCrud.jsx:54-64`).
 */
const AddTenderColumnRenderers = ({ columns }: { columns: Array<Record<string, unknown>> }) => {
    columns.forEach((column) => {
        const key = String(column['key'] ?? '')

        if (key === 'status') {
            column['render'] = (_: unknown, row: unknown): ReactNode => {
                const record = sourceOf(row)

                return <TenderStatus status={record.status} countOffers={record.countOffers} />
            }
        }

        if (key === 'client') {
            column['render'] = (_: unknown, row: unknown): ReactNode => <TenderClientLink client={sourceOf(row).client} />
        }
    })

    return columns
}

/** Довідник-select (`SPEC-007`): кілька рядків — і у фільтрі «будь-який з обраних». */
const dictionary = (label: string, resource: string, isMultiple = false): IFieldConfig => ({
    label,
    type: 'select',
    resource,
    ...(isMultiple ? { isMultiple: true, isFilterMultiple: true } : {}),
})

/** Лише перегляд: ставить сервер (переходи, аудит). */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/**
 * Поля, яких бек у тілі не приймає (`tender:create` / `tender:update`, ДЗ-01,
 * ДЗ-02): статус і причину ставлять переходи, дати й авторів — аудит. Не шлемо
 * їх, щоб тіло запиту не обіцяло зміни, якої не буде.
 */
const READ_ONLY_KEYS = [
    'id',
    'status',
    'closingAt',
    'lossReason',
    'externalCode',
    'isRemoved',
    'countOffers',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

const prepareTenderPayload = ({ values }: { values: Record<string, unknown> }): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    // Порожня ціна — 0: бек тримає NOT NULL (`offeredPrice` default 0)
    const price = values['offeredPrice']

    if (price === null || price === undefined || price === '') values['offeredPrice'] = 0
}

/**
 * Тендер (`SPEC-023` §4.5 — відмінності від запиту `SPEC-022` §8): реєстр на
 * 9 колонок і 8 фільтрів у порядку легасі (`TenderCrud.jsx:29-36`; «Тип авто» —
 * справжні `typesCars`, ДП-09), 20 рядків на сторінку; рядок статусів над
 * таблицею з `GET /services/tenders/counters` (`StatusFilterBar` — сам, за
 * `statusResource`); режим «Канбан» — лише перегляд (§8.2).
 *
 * Права — лише з беку: «Додати» — колекційне `create`
 * (`ResourceCorrespondenceConfig`), «Редагувати» / «Видалити» — `_permissions`
 * запису, кнопки переходів — теж (`TransportRequestButtons`). Ролей ресурс не
 * питає (легасі — `TenderResource.jsx:155-177`, ДП-21).
 */
export const TenderResource: IResourceConfig = {
    title: 'Тендер',
    fields: {
        id: {
            label: L.id,
            type: 'number',
            isEditable: false,
        },
        // Обов'язковий (ДТ-05); у минулому — дозволено, як у легасі (Q23.1)
        deadline: {
            label: L.deadline,
            type: 'date',
            isRequired: true,
            view: renderDateView,
        },
        title: {
            label: L.title,
            // Порожня — бек підставить назву клієнта (ЗП-02)
            inputProps: { maxLength: 255 },
        },
        client: {
            label: L.client,
            type: 'select',
            resource: 'Crm.Client',
            isRequired: true,
        },
        // Статус ставлять створення, ціна й переходи — у формі лише перегляд.
        // `inputProps.disabled` вимикає й ядрову колонку (вартовий колонки статусу)
        status: {
            label: L.status,
            type: 'status',
            statusResource: TENDER_STATUS_RESOURCE,
            isEditable: false,
            inputProps: { disabled: true },
        },
        responsible: {
            label: L.responsible,
            type: 'select',
            resource: 'Setting.User',
            isRequired: true,
            isFilterMultiple: true,
            view: renderUsersView,
        },
        cargo: {
            label: L.cargo,
            type: 'textarea',
            isRequired: true,
            inputProps: { maxLength: 4096 },
        },
        typesCars: { ...dictionary(L.typesCars, 'Setting.ListCarType', true), isRequired: true },
        // Опис — HTML tiptap з таблицями й роздільником (Q2.8, пресет
        // `description`); санітизує бек (`RichTextSanitizer`)
        description: {
            label: L.description,
            input: RichTextInput,
            view: renderRichTextView,
            inputProps: { toolbar: 'description' },
        },
        loadingCountries: { ...dictionary(L.loadingCountries, 'Setting.ListCountry', true), isRequired: true },
        unloadingCountries: { ...dictionary(L.unloadingCountries, 'Setting.ListCountry', true), isRequired: true },
        // Двокроково й у формі створення (G-05): крок 1 — `tender_files`,
        // крок 2 — `fileUuids` у POST/PATCH; білий список — як у договору CRM
        files: {
            label: L.files,
            type: 'file',
            isMultiple: true,
            uploadUrl: TENDER_FILE_UPLOAD_URL,
            uploadAccept: CRM_CONTRACT_FILE_EXTENSIONS,
            uploadMaxSizeMb: CRM_FILE_MAX_SIZE_MB,
            showViewIcon: false,
            showEditIcon: false,
        },
        offeredPrice: {
            label: L.offeredPrice,
            type: 'number',
            defaultValue: 0,
            inputProps: { min: 0, precision: 2, style: { width: '100%' } },
        },
        offeredPriceCurrency: {
            label: L.offeredPriceCurrency,
            type: 'select',
            resource: 'Setting.ListCurrency',
            responseLabel: (item: { code?: unknown; title?: unknown }) =>
                typeof item?.code === 'string' ? item.code : String(item?.title ?? ''),
        },
        closingAt: readOnly({
            label: L.closingAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        lossReason: readOnly(dictionary(L.lossReason, TENDER_LOSS_REASON_RESOURCE)),
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    // 9 колонок у порядку легасі (`TenderCrud.jsx:36`)
    table: {
        showColumns: [
            'id',
            'client',
            'status',
            'loadingCountries',
            'unloadingCountries',
            'typesCars',
            'responsible',
            'deadline',
            'title',
        ],
        dataSourceNoTransform: ['status', 'client'],
        eventHandlers: {
            onBuildColumns: { AddTenderColumnRenderers },
        },
    },
    // 8 фільтрів у порядку легасі (`TenderCrud.jsx:29-33`): «Тип авто» в
    // панелі немає, як у легасі (в API фільтр є)
    filter: {
        includeFields: [
            'id',
            'client',
            'status',
            'loadingCountries',
            'unloadingCountries',
            'responsible',
            'deadline',
            'title',
        ],
        // «Статус» — рядок довідника, як рядок статусів над таблицею: пише код
        fieldProps: {
            status: { input: StatusDictionarySelect },
        },
    },
    list: {
        pagination: { pageSize: 20 },
    },
    /**
     * Режим «Канбан» (`SPEC-022` §8.2, легасі `TenderCrud.jsx:69-75`) — той самий
     * шлях, що в канбані модуля з демки (`SPEC-018`): `headerPage.onAfterInit` дописує варіант у
     * `variantItems` поряд із ядровою таблицею, перемикач малює ядро.
     */
    headerPage: {
        eventHandlers: {
            onAfterInit: {
                AddTenderKanbanMode: ({ headerPageContext }: { headerPageContext?: TenderHeaderPageContextLike }): void => {
                    const crudContext = headerPageContext?.storages?.crudContext
                    const listContext = crudContext?.listContext

                    if (!crudContext || !listContext) return

                    listContext.variantItems = {
                        ...(listContext.variantItems ?? {}),
                        kanban: {
                            label: 'Канбан',
                            icon: TbLayoutKanban,
                            template: TenderKanbanTemplate,
                            props: { crudContext },
                        },
                    }
                },
            },
        },
    },
    form: {
        eventHandlers: {
            // Крок 2: нові файли — `fileUuids`, саме поле — геть із тіла
            onBeforeTransformSendSaveRequest: {
                tenderFileUuids: attachUploadedFiles('files'),
            },
            onBeforeSendSaveRequest: {
                TenderPayload: prepareTenderPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            // Кнопки переходів — лише в перегляді й лише за `_permissions`
            // «Конвертація в заявку» — Hook контейнера заявок: форма створення з
            // полями джерела, саме закриття — на беку при збереженні (`SPEC-021` §5.9)
            view: {
                template: TenderFormTemplate,
                onButtons: createTransportRequestButtons({
                    restUrl: TENDER_REST_URL,
                    lossReasonResource: TENDER_LOSS_REASON_RESOURCE,
                    convert: (record, done) => openApplicationCreate({ source: 'tender', record, onSaved: done }),
                    texts: {
                        loseSuccess: 'Тендер успішно програно.',
                        returnSuccess: 'Тендер успішно повернуто в роботу.',
                    },
                }),
            },
            edit: { template: TenderFormTemplate },
            create: { template: TenderFormTemplate },
        },
    },
    drawer: {
        // Як картка клієнта: два стовпці блоків і поля по два в ряд
        width: TENDER_DRAWER_WIDTH,
    },
    restUrl: TENDER_REST_URL,
    pageUrl: '/tenders',
    pageConfig: {
        title: 'Тендер',
        icon: <MdOutlineGavel />,
    },
    settings: {
        searchFields: ['title'],
    },
}
